import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import {
  cardSurface,
  cardBody,
  cardHeader,
  cardHeaderTitle,
  formLabel,
  primaryBtn,
  secondaryBtn,
} from "./assessmentStyles";

const AssignAssessment = () => {
  const history = useHistory();
  const location = useLocation();

  const [assessments, setAssessments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [assessmentId, setAssessmentId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      const [riu, own, s] = await Promise.all([
        AssessmentService.listAssessments({
          status: "PUBLISHED",
          limit: 200,
          owner_type: "RIU_TEMPLATE",
        }),
        AssessmentService.listAssessments({
          status: "PUBLISHED",
          limit: 200,
          owner_type: "COMPANY_CUSTOM",
        }),
        AssessmentService.listSuppliers(),
      ]);
      const riuItems = riu?.isSuccess ? riu?.data?.data?.items ?? [] : [];
      const ownItems = own?.isSuccess ? own?.data?.data?.items ?? [] : [];
      setAssessments([...ownItems, ...riuItems]);
      if (s?.isSuccess) {
        const rows = s?.data?.data ?? [];
        setSuppliers(Array.isArray(rows) ? rows : []);
      } else {
        setLoadError("Could not load suppliers.");
      }
    })();
  }, []);

  const selectedAssessment = useMemo(
    () =>
      assessments.find((a) => String(a.id) === String(assessmentId)) || null,
    [assessments, assessmentId]
  );

  const matchingSuppliers = useMemo(() => {
    if (!selectedAssessment) return [];
    return suppliers.filter(
      (sup) =>
        Number(sup.industry) === Number(selectedAssessment.industry) &&
        Number(sup.sub_industry) === Number(selectedAssessment.sub_industry)
    );
  }, [suppliers, selectedAssessment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!assessmentId || !supplierId) return;
    setSubmitting(true);
    const source =
      selectedAssessment?.owner_type_key === "COMPANY_CUSTOM"
        ? "company"
        : "riu_backend";
    const payload = {
      assessment_id: Number(assessmentId),
      supplier_id: Number(supplierId),
      source,
      due_at: dueAt || undefined,
    };
    const res = await AssessmentService.assignAssessment(payload);
    setSubmitting(false);
    if (res?.isSuccess) {
      const companyId = JSON.parse(localStorage.getItem("currentUser"))
        ?.company_id;
      history.push(`/assessment/active/${companyId}`);
    }
  };

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle="Assign Assessment"
    >
      <Form onSubmit={handleSubmit}>
        <div style={cardSurface}>
          <div style={cardHeader}>
            <h5 style={cardHeaderTitle}>Assignment</h5>
            <button
              type="button"
              style={secondaryBtn}
              onClick={() => history.goBack()}
            >
              Back
            </button>
          </div>
          <div style={cardBody}>
            {loadError && <Alert variant="danger">{loadError}</Alert>}
            <div style={{ marginBottom: 16 }}>
              <label style={formLabel}>Assessment *</label>
              <Form.Control
                as="select"
                required
                value={assessmentId}
                onChange={(e) => {
                  setAssessmentId(e.target.value);
                  setSupplierId("");
                }}
              >
                <option value="">Select assessment…</option>
                {assessments.map((a) => (
                  <option key={a.id} value={a.id}>
                    [
                    {a.owner_type_key === "RIU_TEMPLATE" ? "Template" : "Custom"}
                    ] {a.title}
                  </option>
                ))}
              </Form.Control>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={formLabel}>Supplier *</label>
              <Form.Control
                as="select"
                required
                disabled={!assessmentId}
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">
                  {assessmentId
                    ? matchingSuppliers.length === 0
                      ? "No suppliers match this industry/sub-industry"
                      : "Select supplier…"
                    : "Pick an assessment first"}
                </option>
                {matchingSuppliers.map((sup) => {
                  const name =
                    sup.register_company_name;
                  return (
                    <option key={sup.id} value={sup.id}>
                      {name}
                    </option>
                  );
                })}
              </Form.Control>
              {assessmentId && selectedAssessment && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: "var(--ink-4)",
                  }}
                >
                  Showing only suppliers matching the assessment's industry &
                  sub-industry.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 4 }}>
              <label style={formLabel}>Due date</label>
              <Form.Control
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                style={{ maxWidth: 220 }}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 18,
          }}
        >
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => history.goBack()}
          >
            Cancel
          </button>
          <button type="submit" style={primaryBtn} disabled={submitting}>
            {submitting ? "Assigning…" : "Assign"}
          </button>
        </div>
      </Form>
    </SupplierManagementShell>
  );
};

export default AssignAssessment;
